<template>
  <div class="currency-amount" data-t="currency-amount">
    <span class="amount" data-t="currency-amount-value">
      {{ formattedAmount }}
    </span>
    <StIcon class="currency-icon" :name="currency?.icon" size="16" />
  </div>
</template>

<script setup lang="ts">
import { currenciesServiceInjectionKey } from './types'
import type { CurrenciesServiceData } from './types'

const {
  currencyId,
  currencyCode,
  amount,
  isSignDisplay = false,
} = defineProps<
  (
    | {
        currencyId: number
        currencyCode?: never
      }
    | { currencyId?: never; currencyCode: string }
  ) & {
    amount: number | string
    isSignDisplay?: boolean
  }
>()
const currenciesService = inject<CurrenciesServiceData>(
  currenciesServiceInjectionKey,
)
const currency = computed(() =>
  currencyId
    ? currenciesService?.getCurrencyById(currencyId)
    : currenciesService?.getCurrencyByCode(
        currencyCode?.toUpperCase() ?? 'USD',
      ),
)
const signDisplay = computed(() => (isSignDisplay ? 'exceptZero' : 'never'))

const { format: formatCrypto } = useCryptoFormatter({
  signDisplay: signDisplay.value,
})

const { format: formatFiat } = useCurrencyFormatter({
  currency: computed(() => currency.value?.code ?? 'USD'),
  signDisplay: signDisplay.value,
})

const isFiat = computed(() => currency.value?.isFiat)
const formattedAmount = computed(() =>
  isFiat.value ? formatFiat(amount) : formatCrypto(amount),
)
</script>

<style scoped>
.currency-amount {
  display: flex;
  gap: var(--spacing-050);
  align-items: center;
}

.currency-icon {
  min-width: 16px;
}

.amount {
  overflow: hidden;
  font: inherit;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
